import { useRef, useMemo, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import { useGLTF } from '@react-three/drei';
import * as THREE from 'three';
import { clone as skeletonClone } from 'three/examples/jsm/utils/SkeletonUtils.js';
import useWorldStore from '../store/worldStore';
import { ZONES } from '../data/zones';
import { CHAT_APP_APPEARANCE_MODELS, getChatAppModelGlb } from '../lib/chatAppCharacters';
import TaskIcon from './TaskIcon';

function getRandomWanderDelay(min, range) {
  return min + Math.random() * range;
}

CHAT_APP_APPEARANCE_MODELS.forEach((model) => {
  useGLTF.preload(model.glb);
});

const ANIM_MAP = {
  idle: 'idle',
  walking: 'walk',
  busy: 'idle',
  dragged: 'grabbed',
};

const FADE_DURATIONS = {
  idle: 0.3,
  walk: 0.2,
  wave: 0.15,
  grabbed: 0.15,
};

const ASSIGNMENT_WAVE_MS = 900;
const ASSIGNMENT_IDLE_MS = 2200;

export default function AgentMesh({ agent, isDragged, dragHandlers }) {
  const glbPath = getChatAppModelGlb(agent.profileDoc?.chat_app_model);
  const gltf = useGLTF(glbPath);
  const assignmentComposer = useWorldStore((s) => s.activeDroppedTaskComposer);
  const groupRef = useRef();
  const currentActionRef = useRef(null);
  const currentKeyRef = useRef(null);
  const positionRef = useRef([...agent.position]);
  const shadowRef = useRef();
  // Life zone wandering
  const wanderTargetRef = useRef(null);
  const wanderTimerRef = useRef(0);
  const wanderAnimRef = useRef('idle'); // 'idle' | 'walking'
  const isHoveredRef = useRef(false);
  const hoverPhaseRef = useRef(null); // null | 'wave' | 'idle'
  const hoverTimerRef = useRef(null);
  const assignmentLoopTimerRef = useRef(null);
  const callWaveRef = useRef(false);
  const prevInCallRef = useRef(false);
  const isAssignmentPreview = assignmentComposer?.agentId === agent.id;

  // Clone scene with independent skeleton per agent
  const clonedScene = useMemo(() => {
    const scene = skeletonClone(gltf.scene);
    scene.traverse((node) => {
      if (node.isMesh) {
        node.castShadow = true;
      }
    });
    return scene;
  }, [gltf.scene]);

  // Create mixer and actions
  const { mixer, actions } = useMemo(() => {
    const m = new THREE.AnimationMixer(clonedScene);
    const a = {};

    const idleClip = gltf.animations.find((c) => c.name === 'idle');
    const walkClip = gltf.animations.find((c) => c.name === 'walking');
    const waveClip = gltf.animations.find((c) => c.name === 'greeting');
    const grabbedClip = gltf.animations.find((c) => c.name === 'grabbed_armature');

    if (idleClip) {
      a.idle = m.clipAction(idleClip);
      a.idle.loop = THREE.LoopRepeat;
      a.idle.clampWhenFinished = false;
    }
    if (walkClip) {
      a.walk = m.clipAction(walkClip);
      a.walk.loop = THREE.LoopRepeat;
      a.walk.clampWhenFinished = false;
    }
    if (grabbedClip) {
      a.grabbed = m.clipAction(grabbedClip);
      a.grabbed.loop = THREE.LoopRepeat;
      a.grabbed.clampWhenFinished = false;
    }
    if (waveClip) {
      a.wave = m.clipAction(waveClip);
      a.wave.loop = THREE.LoopRepeat;
      a.wave.clampWhenFinished = false;
    }

    return { mixer: m, actions: a };
  }, [clonedScene, gltf.animations]);

  // Start idle on mount
  useEffect(() => {
    wanderTimerRef.current = getRandomWanderDelay(3, 8);
  }, []);

  useEffect(() => {
    if (actions.idle) {
      actions.idle.play();
      currentActionRef.current = actions.idle;
      currentKeyRef.current = 'idle';
    }
    return () => mixer.stopAllAction();
  }, [mixer, actions]);

  // Wave when entering a call
  useEffect(() => {
    const unsub = useWorldStore.subscribe((state) => {
      const inCall = (state.activeCalls || []).some((call) => call.agentId === agent.id);
      if (inCall && !prevInCallRef.current) {
        callWaveRef.current = true;
        const waveClip = gltf.animations.find((c) => c.name === 'greeting');
        const dur = waveClip ? waveClip.duration * 1000 : 1500;
        setTimeout(() => { callWaveRef.current = false; }, dur);
      }
      prevInCallRef.current = inCall;
    });
    return unsub;
  }, [agent.id, gltf.animations]);

  useEffect(() => {
    if (isAssignmentPreview) {
      isHoveredRef.current = true;

      const runAssignmentLoop = () => {
        hoverPhaseRef.current = 'wave';
        clearTimeout(hoverTimerRef.current);
        clearTimeout(assignmentLoopTimerRef.current);

        hoverTimerRef.current = setTimeout(() => {
          if (!isHoveredRef.current) return;
          hoverPhaseRef.current = 'idle';

          assignmentLoopTimerRef.current = setTimeout(() => {
            if (isHoveredRef.current) {
              runAssignmentLoop();
            }
          }, ASSIGNMENT_IDLE_MS);
        }, ASSIGNMENT_WAVE_MS);
      };

      runAssignmentLoop();
      return () => {
        clearTimeout(hoverTimerRef.current);
        clearTimeout(assignmentLoopTimerRef.current);
      };
    }

    isHoveredRef.current = false;
    hoverPhaseRef.current = null;
    clearTimeout(hoverTimerRef.current);
    clearTimeout(assignmentLoopTimerRef.current);
    return undefined;
  }, [isAssignmentPreview]);

  useFrame((state, delta) => {
    mixer.update(delta);

    const suspendLifeWander =
      isDragged
      || isAssignmentPreview
      || agent.currentZoneId !== 'life'
      || agent.mode === 'work'
      || agent.state === 'walking'
      || agent.state === 'dragged';

    if (suspendLifeWander) {
      wanderTargetRef.current = null;
      wanderAnimRef.current = 'idle';
    }

    // Determine target animation
    let targetKey;
    if (isDragged) {
      targetKey = 'grabbed';
    } else if (callWaveRef.current) {
      targetKey = 'wave';
    } else if (isAssignmentPreview || isHoveredRef.current) {
      targetKey = hoverPhaseRef.current === 'wave' ? 'wave' : 'idle';
    } else if (wanderAnimRef.current === 'walking') {
      targetKey = 'walk';
    } else {
      targetKey = ANIM_MAP[agent.state] || 'idle';
    }
    const targetAction = actions[targetKey];

    // Crossfade if target changed
    if (targetAction && targetKey !== currentKeyRef.current) {
      const current = currentActionRef.current;
      const fadeDuration = FADE_DURATIONS[targetKey] || 0.3;

      if (current) {
        current.crossFadeTo(targetAction.reset().play(), fadeDuration, true);
      } else {
        targetAction.reset().play();
      }

      currentActionRef.current = targetAction;
      currentKeyRef.current = targetKey;
    }

    // Movement interpolation and position
    if (groupRef.current) {
      const pos = positionRef.current;

      if (isAssignmentPreview) {
        pos[0] = agent.position[0];
        pos[1] = agent.position[1];
        pos[2] = agent.position[2];
      } else if (agent.state === 'walking' && agent.targetPosition && !isHoveredRef.current) {
        const [tx, ty, tz] = agent.targetPosition;
        // Linear constant-speed movement
        const speed = 0.04;
        const dx = tx - pos[0];
        const dy = ty - pos[1];
        const dz = tz - pos[2];
        const dist = Math.sqrt(dx * dx + dz * dz);

        if (dist > 0.15) {
          pos[0] += (dx / dist) * speed;
          pos[1] += (dy / (dist || 1)) * speed;
          pos[2] += (dz / dist) * speed;
        }

        if (dist <= 0.15) {
          pos[0] = tx;
          pos[1] = ty;
          pos[2] = tz;
          useWorldStore.getState().finalizeAgentWalk(agent.id, [...pos]);
          // Update task log to "working"
          const log = useWorldStore.getState().taskLog.find(
            (e) => e.agentId === agent.id && e.state === 'en route'
          );
          if (log) useWorldStore.getState().updateTaskLog(log.id, { state: 'working' });
        }

        useWorldStore.getState().updateAgentPosition(agent.id, [...pos]);

        // Face target
        const angle = Math.atan2(tx - pos[0], tz - pos[2]);
        groupRef.current.rotation.y = angle;
      } else if (agent.state === 'busy' && agent.currentZoneId !== 'life') {
        // Stay at current position in work zone, face zone center
        pos[0] = agent.position[0];
        pos[1] = agent.position[1];
        pos[2] = agent.position[2];

        const zone = ZONES.find((z) => z.id === agent.currentZoneId);
        if (zone) {
          const angle = Math.atan2(
            zone.center[0] - pos[0],
            zone.center[1] - pos[2]
          );
          groupRef.current.rotation.y = angle;
        }
      } else if (isDragged) {
        // Dragged — follow store position (updated by drag handler)
        pos[0] = agent.position[0];
        pos[1] = agent.position[1];
        pos[2] = agent.position[2];
      } else if (agent.currentZoneId === 'life' && !isHoveredRef.current) {
        // Life zone wandering
        wanderTimerRef.current -= delta;

        if (wanderTargetRef.current) {
          // Walking to wander target
          const tx = wanderTargetRef.current[0];
          const tz = wanderTargetRef.current[1];
          const dx = tx - pos[0];
          const dz = tz - pos[2];
          const dist = Math.sqrt(dx * dx + dz * dz);

          if (dist > 0.2) {
            const speed = 0.02;
            pos[0] += (dx / dist) * speed;
            pos[2] += (dz / dist) * speed;
            groupRef.current.rotation.y = Math.atan2(dx, dz);

            if (wanderAnimRef.current !== 'walking') {
              wanderAnimRef.current = 'walking';
            }
          } else {
            // Arrived
            wanderTargetRef.current = null;
            wanderTimerRef.current = getRandomWanderDelay(5, 10);
            wanderAnimRef.current = 'idle';
          }
          useWorldStore.getState().updateAgentPosition(agent.id, [pos[0], pos[1], pos[2]]);
        } else if (wanderTimerRef.current <= 0) {
          // Pick a new wander target within Life zone (radius ~4 from center [0,0])
          const angle = Math.random() * Math.PI * 2;
          const r = getRandomWanderDelay(1, 3.5);
          const tx = Math.cos(angle) * r;
          const tz = Math.sin(angle) * r;

          // Simple collision check — avoid other agents
          const others = useWorldStore.getState().agents.filter((a) => a.id !== agent.id);
          const tooClose = others.some((a) => {
            const adx = a.position[0] - tx;
            const adz = a.position[2] - tz;
            return Math.sqrt(adx * adx + adz * adz) < 1.5;
          });

          if (!tooClose) {
            wanderTargetRef.current = [tx, tz];
          } else {
            wanderTimerRef.current = 2; // Try again in 2s
          }
        } else {
          // Idle — sync from store
          pos[0] = agent.position[0];
          pos[1] = agent.position[1];
          pos[2] = agent.position[2];
          wanderAnimRef.current = 'idle';
        }
      } else {
        // idle — sync from store
        pos[0] = agent.position[0];
        pos[1] = agent.position[1];
        pos[2] = agent.position[2];
      }

      // Face camera when hovered (waving)
      if ((isAssignmentPreview || isHoveredRef.current) && !isDragged) {
        const cam = state.camera.position;
        const angle = Math.atan2(cam.x - pos[0], cam.z - pos[2]);
        groupRef.current.rotation.y = angle;
      }

      let yPos = pos[1];
      if (isDragged) {
        yPos = 1.2 + Math.sin(state.clock.elapsedTime * Math.PI * 4) * 0.08;
      }

      groupRef.current.position.set(pos[0], yPos, pos[2]);

      // Keep shadow pinned to ground level
      if (shadowRef.current) {
        shadowRef.current.position.y = 0.01 - yPos;
        // Brighten shadow while dragging
        shadowRef.current.material.opacity = isDragged ? 0.25 : 0.12;
      }
    }

  });

  return (
    <group
      ref={groupRef}
      onPointerDown={(e) => dragHandlers.onPointerDown(e, agent.id)}
      onPointerEnter={(e) => {
        e.stopPropagation();
        if (useWorldStore.getState().draggedAgentId) return;
        if (isAssignmentPreview) return;
        isHoveredRef.current = true;
        hoverPhaseRef.current = 'wave';
        // Snapshot current position so we can resume from here
        const a = useWorldStore.getState().agents.find((x) => x.id === agent.id);
        if (a) positionRef.current = [...a.position];
        useWorldStore.getState().hoverAgent(agent.id);
        document.body.style.cursor = 'pointer';
        clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = setTimeout(() => {
          if (isHoveredRef.current) hoverPhaseRef.current = 'idle';
        }, 800);
      }}
      onPointerLeave={(e) => {
        e.stopPropagation();
        if (isAssignmentPreview) return;
        isHoveredRef.current = false;
        hoverPhaseRef.current = null;
        clearTimeout(hoverTimerRef.current);
        // Sync positionRef from current store position before resuming
        const a = useWorldStore.getState().agents.find((x) => x.id === agent.id);
        if (a) positionRef.current = [...a.position];
        useWorldStore.getState().unhoverAgent();
        if (!useWorldStore.getState().draggedAgentId) {
          document.body.style.cursor = '';
        }
      }}
    >
      <primitive object={clonedScene} scale={0.3} />

      {/* Invisible hitbox for hover/click detection */}
      <mesh visible={false}>
        <cylinderGeometry args={[0.6, 0.6, 1.5, 8]} />
        <meshBasicMaterial transparent opacity={0} />
      </mesh>

      <TaskIcon agent={agent} zone={ZONES.find((z) => z.id === agent.currentZoneId)} />

      {/* Ground shadow circle */}
      <mesh ref={shadowRef} rotation-x={-Math.PI / 2} position-y={0.01}>
        <circleGeometry args={[0.4, 32]} />
        <meshBasicMaterial color="white" transparent opacity={0.12} />
      </mesh>
    </group>
  );
}
